import { prospectPartyCode, repPartyCode } from "cfg/endpoints";
import { AvatarStyle } from "components/Leaderboard";
import { PromptType } from "./consts";
import uuid from "react-uuid";

////////////////////////////////////////////
// CoachingPromptInfo is the result type of coaching websocket
////////////////////////////////////////////

export type CadenceData = {
    isActive: boolean;
    latest: number;
    average: number;
  }
  
export function jsonSafeToCadenceData(value: Record<string, any>): CadenceData {
    return {
        isActive: value.isActive ?? false,
        latest: value.latest ?? 0,
        average: value.average ?? 0
    }
}

export type TalkTimeInterval = {
    start: number;
    end: number;
}

export function jsonSafeToTalkTimeInterval(value: Record<string, any>): TalkTimeInterval {
    return {
        start: value.start ?? 0,
        end: value.end ?? 0
    }
}

export type CoachingPlay = {
    uuid: string;
    label: string;
    responses: string[];
    active: boolean;
    startTime: number;
    type: PromptType.PLAY
}

export function jsonSafeToCoachingPlay(value: CoachingPromptInfo): CoachingPlay {
    return {
        uuid: value.playId ?? '',
        label: value.playName ?? '',
        responses: Array.isArray(value.promptText) ? value.promptText : (value.promptText?.split('\n') ?? []),
        active: value.active ?? false,
        startTime: new Date().getTime(),
        type: PromptType.PLAY
    }
}

export type CoachingSuggestion = {
    label: string;
    responses: string[];
    startTime: number;
    active: boolean
    uuid: string
    type: PromptType.TRIGGER
}

export function jsonSafeToCoachingSuggestion(value: any): CoachingSuggestion {
    return {
        label: value.trigger_name,
        responses: Array.isArray(value.trigger_prompt) ? value.trigger_prompt : (value.trigger_prompt?.split('\n') ?? []),
        startTime: new Date().getTime(),
        active: true,
        uuid: uuid(),
        type: PromptType.TRIGGER
    }
}

export type CoachingPromptInfo = {
    promptId: number | null;
    promptType: string;
    asOf: number | null;
    promptValue: any;
    promptText: string | string[] | null;
    playId: string | null;
    playName: string | null;
    active: boolean;  
    triggerName: string | null;
    triggerPrompt: string | string[] | null;
}

export type CoachingCadenceData = {
    rep_cadence: CadenceData;
    prospect_cadence: CadenceData;
}

export function jsonSafeToCoachingCadenceData(value: Record<string, any>): CoachingCadenceData {
    return {
        rep_cadence: jsonSafeToCadenceData(value['rep']),
        prospect_cadence: jsonSafeToCadenceData(value['prospect'])
    }
}

export type CoachingIntervalData = {
    rep_intervals: TalkTimeInterval[];
    prospect_intervals: TalkTimeInterval[];
}

export function jsonSafeToCoachingIntervalData(value: Record<string, any>): CoachingIntervalData {
    const repIntervals = value.find((entry: any) => entry.party_code === repPartyCode) ?? []
    const prospectIntervals = value.find((entry: any) => entry.party_code === prospectPartyCode) ?? []
    return {
        rep_intervals: (repIntervals.intervals ?? []).map((interval: any) => jsonSafeToTalkTimeInterval(interval)),
        prospect_intervals: (prospectIntervals.intervals ?? []).map((interval: any) => jsonSafeToTalkTimeInterval(interval)),
    }
}

export function jsonSafeToCoachingPrompt(value: Record<string, any>): CoachingPromptInfo {
    return {
        promptId: value.prompt_id ?? null,
        promptType: value.prompt_type?.toUpperCase() ?? '',
        asOf: value.as_of ?? null,
        promptValue: value.value ?? null,
        promptText: value.prompt_text ?? null,
        playId: value.play_id ?? null,
        playName: value.play_name ?? null,
        active: value.active ?? false,
        triggerName: value.trigger_name ?? null,
        triggerPrompt: value.trigger_prompt ?? null,
    };
}


export type WeatherData = {
  location_str: string | null
  temperature: number | null
  utcoffset: number | null
  icon_url: string | null
}

export function jsonSafeToWeatherData(value: Record<string, any>): WeatherData {
  return {
    location_str: value.location_str ?? null,
    temperature: typeof value.temperature === 'number' ? value.temperature : null,
    utcoffset: typeof value.utcoffset === 'number' ? value.utcoffset : null,
    icon_url: value.icon_url ?? null
  }
}

export enum RoomMetricType {
  TOTAL_CALLS = 'TOTAL_CALLS',
  LIVE_DURATION = 'LIVE_DURATION'
}

export type RoomMetadata = {
  room_id: string | null
  room_name: string
  metric: RoomMetricType
  room_code: string | null
  team_id: string | null
  email_domain: string | null
}

export type GetRoomMembershipsResult = RoomMetadata[]

export type UserLeaderboardResult = {
  user_id: string | null
  avatar_name: string | null
  avatar_style: AvatarStyle | null
  value: number
  rank?: number
}


export function jsonSafeToUserLeaderboardResult(record: Record<string, any>): UserLeaderboardResult {
    return {
      'user_id': record.user_id,
      'avatar_name': record.avatar_name,
      'avatar_style': record.avatar_style,
      'value': record.value,
      'rank': record.rank ?? undefined
    }
}
  
export type LeaderboardDataResult = UserLeaderboardResult[]

export function jsonSafeToLeaderboardDataResult(record:  Record<string, any>): LeaderboardDataResult {
    return record.map((value: Record<string, any>) => jsonSafeToUserLeaderboardResult(value))
  }


export const GLOBAL_ROOM_ID = '__GLOBAL_ROOM_ID'
export const GLOBAL_ROOM_NAME = 'GLOBAL ROOM'
